import { useState, useRef, useEffect } from 'react'
import { Inbox, Archive, Moon, ChevronDown, Check } from 'lucide-react'

type StatusFilterType = 'open' | 'closed' | 'snoozed'

interface StatusFilterDropdownProps {
  currentStatus: StatusFilterType
  stats: {
    open: number
    closed: number
    snoozed: number
  }
  onStatusChange: (status: StatusFilterType) => void
}

const STATUS_OPTIONS: Array<{
  key: StatusFilterType
  label: string
  icon: typeof Inbox
}> = [
  { key: 'open', label: 'Abertas', icon: Inbox },
  { key: 'closed', label: 'Fechadas', icon: Archive },
  { key: 'snoozed', label: 'Adiadas', icon: Moon },
]

export default function StatusFilterDropdown({
  currentStatus,
  stats,
  onStatusChange,
}: StatusFilterDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
    }, 0)

    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const currentOption = STATUS_OPTIONS.find((opt) => opt.key === currentStatus)
  const currentCount = stats[currentStatus]
  const CurrentIcon = currentOption?.icon || Inbox

  return (
    <div ref={containerRef} className="relative">
      {/* Dropdown Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
      >
        <CurrentIcon className="w-4 h-4 text-gray-500" />
        <span>
          {currentCount} {currentOption?.label}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[160px] z-50">
          {STATUS_OPTIONS.map((option) => {
            const Icon = option.icon
            return (
              <button
                key={option.key}
                onClick={() => {
                  onStatusChange(option.key)
                  setIsOpen(false)
                }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors cursor-pointer ${
                  currentStatus === option.key ? 'bg-gray-100' : 'hover:bg-gray-50'
                }`}
              >
                <Icon className="w-4 h-4 text-gray-500" />
                <span className="flex-1 text-left">{option.label}</span>
                {currentStatus === option.key && <Check className="w-4 h-4 text-orange-500" />}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
